import { useEffect, useMemo, useRef } from 'react';
import {
  Button,
  Logo,
  Modal,
  Text,
} from '@insight-health-ai/roseate/components';
import { useWebFormStore } from 'src/hooks/form/useWebFormStore';
import { useAuthStore } from 'src/hooks/auth';
import { useChatValidationStore } from 'src/hooks/chat';
import { useNavigate } from 'react-router-dom';
import { AppLoader } from 'src/components';
import { useTranslation } from 'react-i18next';
import { analytics } from 'src/services/analytics';
import { getAppointmentDetailsForAnalytics } from 'src/utils';
import { parseFormContent, getAllFields } from './types';
import type { FormValues } from './types';
import { FormProvider } from './FormProvider';
import { blockRegistry } from './registry/blocks';

export const WebFormPage = () => {
  const { t } = useTranslation('vca');
  const navigate = useNavigate();
  const {
    activeForm,
    prefillData,
    loading,
    submitting,
    isComplete,
    error,
    fetchNextForm,
    submitForm,
  } = useWebFormStore();
  const { appointment, logout } = useAuthStore();
  const { token } = useChatValidationStore();

  useEffect(() => {
    fetchNextForm();
  }, []);

  const formContent = useMemo(
    () => parseFormContent(activeForm?.content),
    [activeForm]
  );

  const lastFormContentRef = useRef(formContent);
  if (formContent.blocks.length > 0) {
    lastFormContentRef.current = formContent;
  }
  const stableFormContent = lastFormContentRef.current;

  const allFields = useMemo(
    () => getAllFields(formContent.blocks),
    [formContent]
  );

  const initialValues = useMemo(() => {
    const defaults: FormValues = {};
    for (const f of allFields) {
      defaults[f.name] = f.type === 'checkbox' ? false : '';
    }
    if (!activeForm) return defaults;
    const prefill = prefillData[activeForm.form_type];
    if (prefill && typeof prefill === 'object') {
      return { ...defaults, ...(prefill as FormValues) };
    }
    return defaults;
  }, [activeForm, prefillData, allFields]);

  const handleSubmit = async (values: FormValues) => {
    if (!activeForm) return;

    analytics.track('Web Form Submitted', {
      ...getAppointmentDetailsForAnalytics(appointment!),
      form_type: activeForm.form_type,
    });

    await submitForm({
      responses: [
        {
          form_type: activeForm.form_type,
          response: values,
        },
      ],
    });
  };

  const handleLogout = async () => {
    await logout({ chatToken: token! });
    navigate('/logout');
  };

  if (loading) {
    return <AppLoader />;
  }

  if (error && !activeForm) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
        <Text appearance="secondary" className="text-center">
          {stableFormContent.errorMessage ?? error}
        </Text>
        <Button variant="outlined" onClick={() => fetchNextForm()}>
          {stableFormContent.errorRetryLabel ??
            t('chatCompletion.form.retryButton', 'Try Again')}
        </Button>
      </div>
    );
  }

  if (isComplete) {
    return (
      <Modal
        closable={false}
        open
        primaryAction={{
          onClick: handleLogout,
          label:
            stableFormContent.completionButtonLabel ??
            t('chatCompletion.logoutButton'),
        }}
        title={
          <div className="flex flex-col items-center pb-[20px]">
            <Logo className="h-8" />
          </div>
        }
      >
        <Text className="text-center text-lg">
          {stableFormContent.completionMessage ??
            t('chatCompletion.form.successMessage')}
        </Text>
      </Modal>
    );
  }

  if (!activeForm) {
    return null;
  }

  return (
    <FormProvider
      fields={allFields}
      initialValues={initialValues}
      submitting={submitting}
      onSubmit={handleSubmit}
      containerClassName={formContent.containerClassName}
      formClassName={formContent.formClassName}
    >
      {formContent.blocks.map((block, i) => {
        const Block = blockRegistry[block.type];
        return Block ? <Block key={i} block={block} /> : null;
      })}
    </FormProvider>
  );
};
